/**
 * 错误处理器工厂
 *
 */

import { createUnauthorizedCodeChecker } from './constants';
import type { ErrorHandlerConfig, RequestError } from './types';
import { ErrorShowType } from './types';

/**
 * 创建错误抛出器 (errorThrower)
 *
 * 用于 UmiJS request 插件的 errorConfig.errorThrower。
 * 当 API 返回非成功状态码时，将响应转换为可识别的业务错误抛出。
 *
 * @param successCode - 成功状态码，默认 '200'
 * @returns errorThrower 函数
 */
export function createErrorThrower(successCode: string | number = '200') {
  return (res: Record<string, unknown>) => {
    const response = res as Record<string, unknown> & {
      code?: string | number;
      errorCode?: string | number;
      data?: unknown;
      msg?: string;
      message?: string;
      errorMessage?: string;
    };

    const codeStr = String(response?.code ?? response?.errorCode);
    const isSuccess = codeStr === String(successCode);

    if (!isSuccess) {
      const errorMsg =
        response?.msg ||
        response?.errorMessage ||
        response?.message ||
        'Request failed';
      const error = new Error(errorMsg) as RequestError;
      error.name = 'BizError';
      error.info = {
        errorCode: response?.code ?? response?.errorCode,
        errorMessage: errorMsg,
        showType: ErrorShowType.ERROR_MESSAGE,
        data: response?.data,
      };
      throw error;
    }
  };
}

/**
 * 创建错误处理器 (errorHandler)
 *
 * 用于 UmiJS request 插件的 errorConfig.errorHandler。
 *
 * @param config - 错误处理配置
 * @returns errorHandler 函数
 */
export function createErrorHandler(config: ErrorHandlerConfig) {
  const {
    onUnauthorized,
    onError,
    onBizError,
    unauthorizedCodes = [],
    includeDefaultUnauthorizedCodes = true,
  } = config;
  const isUnauthorized = createUnauthorizedCodeChecker(
    unauthorizedCodes,
    includeDefaultUnauthorizedCodes,
  );

  return (
    error: RequestError & { _unauthorizedHandled?: boolean },
    opts?: { skipErrorHandler?: boolean },
  ) => {
    // 跳过错误处理
    if (opts?.skipErrorHandler) throw error;

    // 已在响应拦截器中处理的未授权错误，跳过重复处理
    if (error._unauthorizedHandled) return;

    // 业务错误
    if (error.name === 'BizError' && error.info) {
      const errorInfo = error.info;
      const { errorMessage, errorCode, showType } = errorInfo;

      // 检查未授权
      if (isUnauthorized(errorCode)) {
        onUnauthorized();
        return;
      }

      // 自定义业务错误处理
      if (onBizError) {
        onBizError(errorInfo);
        return;
      }

      // 默认: 按 showType 显示错误
      if (onError && errorMessage) {
        onError(errorMessage, showType as ErrorShowType);
      }
      return;
    }

    // HTTP 响应错误
    if (error.response) {
      const status = error.response.status;

      if (isUnauthorized(status)) {
        onUnauthorized();
        return;
      }

      onError?.(`Request failed (${status})`);
      return;
    }

    // 请求已发送但无响应
    if (error.request) {
      onError?.('No response from server');
      return;
    }

    // 其他错误
    onError?.(error.message || 'Request failed');
  };
}
